import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import axiosInstance from '../../services/AxiosInstance';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import {
  MODULE_NOTIFICATION_PAGES,
  notifyNotificationsUpdated,
} from '../layouts/nav/notificationScope';
import {
  getNotificationMessage,
  getNotificationModuleLabel,
  getNotificationTargetPath,
} from '../../utils/notificationMessages';
import '../../css/SearchFilterPanel.css';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './NotificationsPage.css';

const MODULE_FILTERS = [
  { key: 'all', label: 'All modules', tableName: null },
  { key: 'tasks', label: 'Tasks', tableName: MODULE_NOTIFICATION_PAGES.tasks.tableName },
  { key: 'forum', label: 'Forum', tableName: MODULE_NOTIFICATION_PAGES.forum.tableName },
  { key: 'documents', label: 'Documents', tableName: MODULE_NOTIFICATION_PAGES.documents.tableName },
];

const READ_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

const EMPTY_PANEL_FILTERS = {
  module: 'all',
  status: 'all',
};

const PAGE_SIZE = 20;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterRef = useRef(null);

  const moduleFilter = searchParams.get('module') || 'all';
  const readFilter = searchParams.get('status') || 'all';

  const [searchDropDown, setSearchDropDown] = useState(false);
  const [panelFilters, setPanelFilters] = useState({
    module: moduleFilter,
    status: readFilter,
  });

  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const userId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.id || null;
    } catch {
      return null;
    }
  }, []);

  const activeModule = MODULE_FILTERS.find((m) => m.key === moduleFilter) || MODULE_FILTERS[0];
  const hasActiveFilters = moduleFilter !== 'all' || readFilter !== 'all';

  const fetchNotifications = useCallback(async (pageNum = 1) => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(PAGE_SIZE),
      });
      if (activeModule.tableName) {
        params.set('TableName', activeModule.tableName);
      }
      if (readFilter === 'unread') params.set('Is_Read', 'false');
      if (readFilter === 'read') params.set('Is_Read', 'true');

      const res = await axiosInstance.get(`/notification/list/${userId}?${params.toString()}`);
      const list = res.data?.notifications || [];
      const pagination = res.data?.pagination || {};
      setNotifications(list);
      setPage(pagination.page || pageNum);
      setTotalPages(pagination.totalPages || 1);
      setTotal(pagination.total || list.length);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [userId, activeModule.tableName, readFilter]);

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  useEffect(() => {
    if (searchDropDown) {
      setPanelFilters({
        module: moduleFilter,
        status: readFilter,
      });
    }
  }, [searchDropDown, moduleFilter, readFilter]);

  useEffect(() => {
    if (!searchDropDown) return undefined;
    const onPointerDown = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        if (e.target.closest?.('.notifications-filter-toggle')) return;
        setSearchDropDown(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [searchDropDown]);

  const applyFiltersToUrl = (module, status) => {
    const next = new URLSearchParams();
    if (module && module !== 'all') next.set('module', module);
    if (status && status !== 'all') next.set('status', status);
    setSearchParams(next);
  };

  const handlePanelChange = (field, value) => {
    setPanelFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    applyFiltersToUrl(panelFilters.module, panelFilters.status);
    setSearchDropDown(false);
  };

  const clearFilters = () => {
    setPanelFilters(EMPTY_PANEL_FILTERS);
    applyFiltersToUrl('all', 'all');
    setSearchDropDown(false);
  };

  const markAsRead = async (notificationId, { silent = false } = {}) => {
    if (!userId || !notificationId) return false;
    try {
      if (!silent) setMarkingId(notificationId);
      await axiosInstance.post('/notification/read', {
        id: notificationId,
        userid: userId,
      });
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        )
      );
      notifyNotificationsUpdated();
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    } finally {
      if (!silent) setMarkingId(null);
    }
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    try {
      setMarkingAll(true);
      const params = new URLSearchParams();
      if (activeModule.tableName) params.set('TableName', activeModule.tableName);
      const qs = params.toString();
      await axiosInstance.put(
        `/notification/user/${userId}/read-all${qs ? `?${qs}` : ''}`
      );
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: n.read_at || new Date().toISOString() }))
      );
      notifyNotificationsUpdated();
      if (readFilter === 'unread') {
        fetchNotifications(1);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleOpen = async (notif) => {
    if (!notif?.is_read) {
      await markAsRead(notif.id, { silent: true });
    }
    const path = getNotificationTargetPath(notif);
    if (path) navigate(path);
  };

  const unreadOnPage = notifications.filter((n) => !n.is_read).length;

  return (
    <>
      <Row className="notifications-page-row">
        <Col lg={12}>
          <Card className="user-list-card notifications-page-card">
            <Card.Header className="notifications-page-card-header">
              <div className="notifications-page-header-content">
                <div>
                  <h4 className="notifications-page-title mb-1">Notifications</h4>
                  <p className="notifications-page-subtitle mb-0">
                    Review activity across modules and mark items as read.
                  </p>
                </div>
                <div className="notifications-page-header-actions">
                  <button
                    type="button"
                    className={`btn notifications-filter-toggle${hasActiveFilters ? ' has-active' : ''}`}
                    onClick={() => setSearchDropDown((open) => !open)}
                    title="Filters"
                    aria-label="Filters"
                    aria-expanded={searchDropDown}
                  >
                    <i className="fas fa-filter me-2" aria-hidden="true" />
                    Filters
                  </button>
                  <button
                    type="button"
                    className="new-user-btn notifications-mark-all-btn"
                    onClick={markAllAsRead}
                    disabled={markingAll || unreadOnPage === 0}
                  >
                    <i className="fas fa-check-double me-2" aria-hidden="true" />
                    {markingAll ? 'Marking…' : 'Mark all as read'}
                  </button>
                </div>
              </div>

              {searchDropDown && (
                <div className="filter-wrapper notifications-filter-panel" ref={filterRef}>
                  <div className="content">
                    <div className="main-filters">
                      <div className="filter-form">
                        <div className="filter-row">
                          <div className="filter-group">
                            <label htmlFor="notifications-filter-module">Module</label>
                            <select
                              id="notifications-filter-module"
                              value={panelFilters.module}
                              onChange={(e) => handlePanelChange('module', e.target.value)}
                            >
                              {MODULE_FILTERS.map((filter) => (
                                <option key={filter.key} value={filter.key}>
                                  {filter.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="filter-group">
                            <label htmlFor="notifications-filter-status">Status</label>
                            <select
                              id="notifications-filter-status"
                              value={panelFilters.status}
                              onChange={(e) => handlePanelChange('status', e.target.value)}
                            >
                              {READ_FILTERS.map((filter) => (
                                <option key={filter.key} value={filter.key}>
                                  {filter.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="filter-actions">
                        <button type="button" className="search-btn" onClick={applyFilters}>
                          <i className="fas fa-search me-2" aria-hidden="true" />
                          Apply Filters
                        </button>
                        <button type="button" className="reset-btn" onClick={clearFilters}>
                          <i className="fas fa-times me-2" aria-hidden="true" />
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card.Header>

            <Card.Body className="notifications-page-card-body">
              <div className="notifications-page-meta mb-3">
                {loading ? 'Loading…' : `${total} notification${total === 1 ? '' : 's'}`}
              </div>

              {loading && notifications.length === 0 ? (
                <div className="loading-container">
                  <div className="loading-spinner" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="empty-state text-center py-5">
                  <i className="fas fa-bell empty-state-icon mb-3" aria-hidden="true" />
                  <p className="mb-0 text-muted">No notifications found.</p>
                </div>
              ) : (
                <div className="notifications-page-list">
                  {notifications.map((notif) => {
                    const isUnread = !notif.is_read;
                    return (
                      <div
                        key={notif.id}
                        className={`notifications-page-item${isUnread ? ' is-unread' : ''}`}
                      >
                        <button
                          type="button"
                          className="notifications-page-item-main"
                          onClick={() => handleOpen(notif)}
                        >
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
                            <span className="badge notifications-module-badge">
                              {getNotificationModuleLabel(notif)}
                            </span>
                            <small className="text-muted">
                              {notif.created_at && moment(notif.created_at).isValid()
                                ? moment(notif.created_at).format('D MMM YYYY, h:mm A')
                                : 'Date unavailable'}
                            </small>
                          </div>
                          <div className="notifications-page-message">
                            {getNotificationMessage(notif)}
                          </div>
                        </button>
                        <div className="notifications-page-item-actions">
                          {isUnread ? (
                            <button
                              type="button"
                              className="new-user-btn notifications-action-btn"
                              disabled={markingId === notif.id}
                              onClick={() => markAsRead(notif.id)}
                            >
                              {markingId === notif.id ? 'Saving…' : 'Mark as read'}
                            </button>
                          ) : (
                            <span className="badge bg-light text-muted">Read</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {totalPages > 1 && (
                <div className="notifications-page-pager d-flex justify-content-center align-items-center gap-3 mt-4">
                  <button
                    type="button"
                    className="new-user-btn notifications-action-btn"
                    disabled={page <= 1 || loading}
                    onClick={() => fetchNotifications(page - 1)}
                  >
                    Previous
                  </button>
                  <span className="text-muted small">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="new-user-btn notifications-action-btn"
                    disabled={page >= totalPages || loading}
                    onClick={() => fetchNotifications(page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <InternalFooter />
    </>
  );
};

export default NotificationsPage;
